/**
 * UIChromeCanvas — the engine-material UI layer (BL-37, ART §9).
 *
 * ONE full-viewport `pointer-events:none` canvas, mounted *behind* the DOM
 * panels (the panels become transparent text+interaction layers on top). It
 * draws the lit panel chrome with the real engine material (`UIMaterial` →
 * `emissiveFill` + `glowCache`), aligned to the live DOM panel rects, and
 * **caches** the result — the chrome is redrawn only on register / resize /
 * layout change, never per-frame. A value-flash or a settling panel runs a
 * short RAF that composites the cached base + the animated dirty region, then
 * stops; static cost is ≈ 0/frame.
 *
 * This single canvas is the source BL-37b/S87 promotes to the *shared WebGL
 * bloom pipeline* — a 2nd instance of the arena's bloom pass consumes it with
 * zero re-architecture. The arena's own `WebGL2Effects.render(gameCanvas)` pass
 * is **not touched** by anything here.
 *
 * Readability gate (ART §9): glow lives only on the chrome (panel edges /
 * material / glints). DOM text/numbers stay crisp on the transparent panels
 * above this canvas — glow never washes text because text is on a higher layer.
 *
 * Reduced-motion (BL-45): the value-flash / settle animation checks
 * `isReducedMotion()` — when damped, panels still appear and values still
 * update, but the glint/pulse animation is skipped.
 */
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { drawLitPanel, type UIRect } from "./UIMaterial.ts";
import { snap, settle, decay } from "../rendering/AnimationUtils.ts";
import { UI_DURATION, type PanelState } from "./uiTokens.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { getStageFit } from "../utils/stage.ts";

interface PanelReg {
  id: number;
  el: HTMLElement;
  colour?: string | undefined;
  /** Live state resolver (active/disabled) — read on every relayout. */
  getState?: (() => PanelState) | undefined;
  /** Static intensity (e.g. a header sub-panel slightly hotter). */
  intensity: number;
  /** Cached last-measured rect (CSS px, viewport coords). */
  rect: UIRect | null;
}

interface Pulse {
  rect: UIRect;
  colour?: string | undefined;
  start: number;
  duration: number;
  /** `flash` = snap-in→decay-out glint; `settle` = settle-in fade. */
  kind: "flash" | "settle";
}

let nextId = 1;

/**
 * A post-processor for the composited chrome (BL-37b / S87). The chrome canvas
 * hands its freshly-drawn frame to `process()` and, if a result comes back,
 * composites it on top (below the DOM text → text never blooms).
 *
 * Declared here as a plain structural interface so the chrome layer stays free
 * of any WebGL dependency (the arena-isolation guardrail / `ui-chrome-isolation`
 * test): the real implementation is `UIBloom` (a 2nd `WebGL2Effects` instance),
 * injected via {@link UIChromeCanvas.setPostFx}.
 */
export interface UIChromePostFx {
  /**
   * Bloom (or otherwise post-process) the source chrome canvas. Returns a
   * canvas to composite over the source, or `null` to show the raw chrome
   * (disabled / degraded / context-lost — the BL-44 fallback).
   */
  process(source: HTMLCanvasElement): HTMLCanvasElement | null;
  /** Tell the post-fx the source DPR (resolution math follows the chrome). */
  setDpr(dpr: number): void;
}

export class UIChromeCanvas {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  /** Offscreen cache of the static chrome — drawn once per relayout. */
  private base: HTMLCanvasElement | null = null;
  private baseCtx: CanvasRenderingContext2D | null = null;
  private panels: PanelReg[] = [];
  private pulses: Pulse[] = [];
  private dpr = 1;
  private raf = 0;
  private relayoutScheduled = false;
  private resizeObserver: ResizeObserver | null = null;
  private mounted = false;
  /** BL-44 degrade rung: when true, chrome draws flat (no `glowCache` stamps). */
  private degraded = false;
  /** BL-37b: optional shared-WebGL bloom post-processor (the 2nd instance). */
  private postFx: UIChromePostFx | null = null;

  /** Mount the canvas behind the DOM panels. Idempotent; safe in headless. */
  mount(): void {
    if (this.mounted || typeof document === "undefined") return;
    this.mounted = true;

    const canvas = document.createElement("canvas");
    canvas.id = "ui-chrome-canvas";
    // Full-viewport, behind the in-flow panels (z-index:-1 paints above the
    // body background but below the static panel blocks), click-through.
    canvas.style.cssText =
      "position:fixed;inset:0;width:100%;height:100%;z-index:-1;" +
      "pointer-events:none;";
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.base = document.createElement("canvas");
    this.baseCtx = this.base.getContext("2d");
    document.body.appendChild(canvas);

    this.resize();
    window.addEventListener("resize", this.onResize);
    // A layout-change observer: panels opening/closing/reflowing re-aligns the
    // chrome. Debounced via `scheduleRelayout`.
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.scheduleRelayout());
      this.resizeObserver.observe(document.body);
    }
  }

  private onResize = (): void => {
    this.resize();
  };

  /** Size the canvas + base to the viewport at DPR, then redraw. */
  private resize(): void {
    if (!this.canvas || typeof window === "undefined") return;
    this.dpr = displayDpr();
    const w = window.innerWidth;
    const h = window.innerHeight;
    for (const c of [this.canvas, this.base]) {
      if (!c) continue;
      c.width = Math.round(w * this.dpr);
      c.height = Math.round(h * this.dpr);
    }
    // The bloom's working resolution follows the chrome's DPR (BL-37b).
    this.postFx?.setDpr(this.dpr);
    // Clip this viewport-sized canvas to the fixed-stage rect so panel glow /
    // bloom never bleeds into the letterbox bars around the scaled stage (it
    // looks like stray lighting "around the window"). With no stage (identity
    // fit) this resolves to inset(0…) — i.e. no clipping. In-stage glow is kept.
    const f = getStageFit();
    const right = Math.max(0, w - f.offsetX - f.designW * f.scale);
    const bottom = Math.max(0, h - f.offsetY - f.designH * f.scale);
    this.canvas.style.clipPath = `inset(${Math.max(0, f.offsetY)}px ${right}px ${bottom}px ${Math.max(0, f.offsetX)}px)`;
    this.relayout();
  }

  /** Register a DOM element as a lit panel. Returns an unregister handle. */
  register(
    el: HTMLElement,
    opts: {
      colour?: string;
      getState?: () => PanelState;
      intensity?: number;
    } = {},
  ): () => void {
    const reg: PanelReg = {
      id: nextId++,
      el,
      colour: opts.colour,
      getState: opts.getState,
      intensity: opts.intensity ?? 1,
      rect: null,
    };
    this.panels.push(reg);
    this.scheduleRelayout();
    return () => {
      this.panels = this.panels.filter((p) => p.id !== reg.id);
      this.scheduleRelayout();
    };
  }

  /** Coalesce multiple invalidations into one rAF redraw. */
  scheduleRelayout(): void {
    if (this.relayoutScheduled || typeof requestAnimationFrame === "undefined") {
      // Headless / no rAF — relayout synchronously so tests see the effect.
      if (typeof requestAnimationFrame === "undefined") this.relayout();
      return;
    }
    this.relayoutScheduled = true;
    requestAnimationFrame(() => {
      this.relayoutScheduled = false;
      this.relayout();
    });
  }

  /** Re-measure every panel rect and redraw the cached base chrome. */
  relayout(): void {
    if (!this.baseCtx || !this.base) return;
    const ctx = this.baseCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.base.width, this.base.height);
    ctx.scale(this.dpr, this.dpr);
    for (const p of this.panels) {
      const r = p.el.getBoundingClientRect();
      p.rect = { x: r.left, y: r.top, w: r.width, h: r.height };
      if (r.width <= 0 || r.height <= 0) continue;
      drawLitPanel(ctx, {
        rect: p.rect,
        colour: p.colour,
        state: p.getState?.() ?? "default",
        intensity: p.intensity,
        glow: !this.degraded,
      });
    }
    this.present();
  }

  /** Composite the cached base (+ any active pulses) onto the visible canvas. */
  private present(): void {
    if (!this.ctx || !this.canvas || !this.base) return;
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.base, 0, 0);
    if (this.pulses.length > 0) {
      ctx.scale(this.dpr, this.dpr);
      const now = nowMs();
      for (const pulse of this.pulses) {
        const t = Math.min(1, (now - pulse.start) / pulse.duration);
        // flash: snap up to a hot peak, decay back. settle: settle-in fade.
        const k =
          pulse.kind === "flash"
            ? snap(Math.min(1, t * 2)) * (1 - decay(t))
            : settle(t);
        drawLitPanel(ctx, {
          rect: pulse.rect,
          colour: pulse.colour,
          state: "active",
          intensity: 1 + k * 1.5,
          glow: !this.degraded,
          fillSurface: false,
        });
      }
    }
    this.applyBloom();
  }

  /**
   * BL-37b / S87 — route the just-composited chrome through the shared WebGL
   * bloom and composite the result back on top. The bloom output is alpha-aware
   * (bloom-only contribution, luminance alpha), so it adds glow over the hot
   * chrome edges while transparent chrome regions stay transparent — exactly
   * how the arena's `#webgl-canvas` is drawn back over `#game-canvas`. Sits
   * BELOW the DOM text layer, so text is never a bloom source and never softens.
   *
   * Dirty-redraw: `present()` only runs on relayout / register / resize / an
   * active pulse, so the bloom pass costs ≈ 0 when the HUD is static. A `null`
   * result (disabled / context-lost / quality off|low → BL-44) leaves the raw
   * S86 chrome visible — still lit, never blank.
   */
  private applyBloom(): void {
    if (!this.postFx || !this.ctx || !this.canvas) return;
    const bloomed = this.postFx.process(this.canvas);
    if (!bloomed) return;
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // Source-over with the bloom's luminance alpha — the same composite the
    // arena uses (a soft additive-feeling glow, not an opaque overwrite). The
    // post-fx output runs at a capped resolution; upscale to the backing store.
    ctx.drawImage(bloomed, 0, 0, this.canvas.width, this.canvas.height);
  }

  /** Drive the short-lived pulse animation; stops itself when none remain. */
  private tick = (): void => {
    const now = nowMs();
    this.pulses = this.pulses.filter((p) => now - p.start < p.duration);
    this.present();
    if (this.pulses.length > 0) {
      this.raf = requestAnimationFrame(this.tick);
    } else {
      this.raf = 0;
      this.present(); // settle back to the clean cached base
    }
  };

  private startTicking(): void {
    if (this.raf || typeof requestAnimationFrame === "undefined") return;
    this.raf = requestAnimationFrame(this.tick);
  }

  /**
   * Value-flash a HUD numeric's chrome: an emissive `snap`→`decay` glint on the
   * chrome layer at the element's rect (the DOM kick is the caller's). Instant
   * (no-op glint) under reduced-motion — the value still updates.
   */
  flashValue(el: HTMLElement, colour?: string): void {
    if (isReducedMotion()) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    this.pulses.push({
      rect: { x: r.left, y: r.top, w: r.width, h: r.height },
      colour,
      start: nowMs(),
      duration: UI_DURATION.flash,
      kind: "flash",
    });
    this.startTicking();
  }

  /** Settle-in glint for a panel/overlay appearing (chrome animates with the
   *  DOM slide). Skipped under reduced-motion. */
  settleIn(el: HTMLElement, colour?: string): void {
    if (isReducedMotion()) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    this.pulses.push({
      rect: { x: r.left, y: r.top, w: r.width, h: r.height },
      colour,
      start: nowMs(),
      duration: UI_DURATION.settle,
      kind: "settle",
    });
    this.startTicking();
  }

  /**
   * Attach (or clear) the shared-WebGL bloom post-processor (BL-37b / S87). The
   * chrome stays the bloom *source*; the bloomed result is composited back onto
   * this same canvas in {@link present}. Passing `null` (or a `process` that
   * returns `null`) shows the raw S86 chrome — the BL-44 graceful fallback.
   */
  setPostFx(fx: UIChromePostFx | null): void {
    this.postFx = fx;
    fx?.setDpr(this.dpr);
    // Re-composite immediately so the change is visible without waiting for the
    // next relayout / pulse (cheap — reuses the cached base).
    this.present();
  }

  /** BL-44 degrade hook: drop the chrome glow → flat lit edges under load. */
  setDegraded(on: boolean): void {
    if (this.degraded === on) return;
    this.degraded = on;
    this.relayout();
  }

  isDegraded(): boolean {
    return this.degraded;
  }

  /** Count of currently-animating pulses (value-flash / settle). Test hook +
   *  a cheap "is the dirty-region RAF live?" probe. */
  activePulseCount(): number {
    return this.pulses.length;
  }

  /** Registered panel count (test hook). */
  panelCount(): number {
    return this.panels.length;
  }

  /** Measure a single base redraw in ms (for the §6.10 budget readout). */
  measureRedraw(): number {
    const t0 = nowMs();
    this.relayout();
    return nowMs() - t0;
  }

  destroy(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    window.removeEventListener?.("resize", this.onResize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
    this.base = null;
    this.baseCtx = null;
    this.panels = [];
    this.pulses = [];
    this.postFx = null;
    this.mounted = false;
  }
}

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Shared global instance — the one full-viewport UI-chrome layer (mirrors the
 *  `glowCache` singleton pattern). */
export const uiChrome = new UIChromeCanvas();
